import { Injectable } from '@angular/core';
import {
  Auth,
  createUserWithEmailAndPassword,
  authState,
  sendEmailVerification,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  UserCredential,
  linkWithPopup,
  deleteUser,
  AuthProvider,
  unlink
} from '@angular/fire/auth';
import { environment } from 'src/environments/environment';
import * as firebase from 'firebase/compat/app';
import { from, throwError, of, Observable } from 'rxjs';
import { catchError, map, take, tap, switchMap } from 'rxjs/operators';
import { BaseService } from './base-service';
import { Credential } from '../models/authentication.model';
import { Store } from '@ngrx/store';
import { selectSessionId } from '../store/selectors/session.selectors';
import { EntityService } from 'src/app/features/entity/services/entity.service';
import { ApiService } from './api.service';
import { mapPayload } from '../classes/operators';

@Injectable()
export class AuthenticationService extends BaseService {

  constructor(
    private af: Auth,
    private store: Store<any>,
    private sEntity: EntityService,
    private api: ApiService
  ) {
    super();
    this.af.languageCode = 'pt-BR';
  }


  public get loggedUser() {
    return this.af.currentUser;
  }

  /** Create a new user with email and password into firebase authentication service
   * @param user user to be created
   * @returns Promise<firebase.fbAuth.UserCredential>
   * @throws <string> error message
   */
  public createAuthUser(credential: Credential): Observable<boolean> {
    return from(createUserWithEmailAndPassword(this.af, credential.email, credential.password)).pipe(
      take(1),
      map(auth => !!auth),
      catchError(err => {
        throw this.translateErrorMessage(err);
      })
    );
  }

  public currentUser() {

    return authState(this.af).pipe(
      take(1),
      catchError(err => throwError(this.translateErrorMessage(err)))
    );
  }


  public async refreshUser() {

    const reloaded = await this.af.currentUser.reload();
    this.af.updateCurrentUser(this.af.currentUser);
    return reloaded;

  }

  /**
   * Send and e-mail to user to confirm account
   */
  public sendEmailVerification() {
    if (!this.af.currentUser) {
      return of(false);
    }

    return authState(this.af).pipe(
      take(1),
      switchMap(user => from(sendEmailVerification(user))),
      map(() => true),
      catchError(err => throwError(this.translateErrorMessage(err)))
    );
  }

  /** Execute the loggin flow with email and password
   * @async
   * @param email User email address
   * @param password User Password
   * @throws <string> error message
   */
  public signInWithEmail(credential: Credential) {
    if (environment.environment == 'local' && credential.email == 'fakeUser') {
      localStorage.setItem('fu_local', credential.password);
      const res: any = {
        user: {
          email: 'fakeuser' + '-' + credential.password,
          emailVerified: true
        }
      };
      return of(res);
    }

    return from(signInWithEmailAndPassword(this.af, credential.email, credential.password)).pipe(
      take(1),
      catchError(err => throwError(this.translateErrorMessage(err)))
    );

  }

  public async recoverPassword(email: string) {
    return sendPasswordResetEmail(this.af, email)
      .catch((err: Error) => {
        throw this.translateErrorMessage(err);
      });
  }

  public isEmailVerified(user: firebase.default.User): boolean {
    const provider = user.providerData[0];
    if (provider.providerId === 'password' && !user.emailVerified) {
      return false;
    }
    return true;
  }

  public signOut() {
    if (environment.environment == 'local') {
      const user = this.sEntity.loggedUser;
      if (user) {
        localStorage.removeItem('localfbuser-' + user.email);
      }
      localStorage.removeItem('logged_email');

    }
    localStorage.removeItem('fu_local');
    this.api.invalidateCacheGroups(['user', 'entity']);

    // get device fingerprint to unsubscribe all topics on the server;
    return this.store.select(selectSessionId).pipe(
      take(1),
      tap(sessionId => localStorage.setItem('loggout_requested', sessionId)),
      switchMap(() => from(this.af.signOut()).pipe(
        take(1),
        map(() => true)
      ))
    );
  }

  signInFacebook(): Observable<UserCredential> {
    const provider = new FacebookAuthProvider();
    // provider.addScope('public_profile');
    provider.addScope('email');

    return from(signInWithPopup(this.af, provider))
      .pipe(
        take(1),
        catchError((err: Error) => {
          throw this.translateErrorMessage(err);
        }));

  }

  signInGoogle(): Observable<UserCredential> {
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');

    return from(signInWithPopup(this.af, provider))
      .pipe(
        take(1),
        map((result) => {
          return result;
        }),
        catchError((err => {
          throw this.translateErrorMessage(err);
        }))
      );
  }

  async reauthenticate(password: string) {

  }

  async deleteProvider() {
    try {
      await deleteUser(this.af.currentUser);
      this.signOut();
    } catch (err) {
      throw this.translateErrorMessage(err);
    }
  }

  /** Translate the messages received from error class
   * @internal
   * @returns Error
   */
  private translateErrorMessage(error: any): Error {

    const traducoes = {
      'auth/email-already-in-use': 'Este endereço de email já está sendo utilizado por outro usuário.',
      'auth/wrong-password': 'Email ou Senha inválidos',
      'auth/user-not-found': 'Email ou Senha inválidos',
      'auth/invalid-email': 'Email inválido',
      'auth/network-request-failed': 'ocorreu um erro na rede ao efetuar a transação.',
      'auth/popup-closed-by-user': 'Login cancelado',
      'auth/operation-not-allowed': 'Operação não permitida, utilize outro método de login',
      'auth/credential-already-in-use': 'Esta conta já está em uso'
    };

    if (traducoes[error.code]) {
      error.message = traducoes[error.code];
    } else {
      if (!environment.production) {
        console.warn('erro sem tradução', error.code, error.message);
      }

    }
    return error;
  }

  async linkGoogleAccount() {
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');
    return this.linkAccount(provider);

  }

  async linkFacebookAccount() {
    const provider = new FacebookAuthProvider();
    provider.addScope('public_profile');
    provider.addScope('email');

    return this.linkAccount(provider);

  }

  linkWithPassword(email: string, password: string) {

    return this.api.post(`users/link_with_password`, { password }).pipe(mapPayload);
  }



  async linkAccount(provider: AuthProvider) {
    try {

      let credential: UserCredential;
      credential = await linkWithPopup(this.af.currentUser, provider);
      await this.refreshUser();

      return credential;
    } catch (err) {
      throw this.translateErrorMessage(err);
    }

  }

  async unlinkProvider(providerID: string) {
    try {
      const removed = await unlink(this.af.currentUser, providerID);
      await this.refreshUser();
      return removed;
    } catch (err) {
      throw this.translateErrorMessage(err);
    }
  }


}
